import { Box, Button, Checkbox, Icon } from '@palmetto/palmetto-components';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';

import {
  QueueStatus,
  queueStatusMap,
  ProgramType,
  UserInfo,
  DomesticContentEligibilityTypes,
  AIPackageReviewStatus,
  QueueHistoryAction,
} from 'types';

import DateTimeDisplay from '../DateTime';

interface GetQueueTableColumnsProps {
  queueProps: any;
  extraColumns?: any;
  removeClaim: (assignee: UserInfo, id: string) => void;
  claimQueue: (id: string) => void;
  handleQueueRowSelection: (queueIds: string[], selected: boolean) => void;
  handleAllQueueRowSelection: () => void;
  selectedQueueIds: string[];
  queues: any[];
  showCheckbox: boolean;
  qcReviewHoldMotion: boolean;
  isFirstSubmittedSort: boolean;
  isOnHoldSort: boolean;
  isHoldQueueView: boolean;
  showTargetReviewTime?: boolean;
}

interface AssigneeCellProps {
  cell: UserInfo | undefined;
  id: string;
  onRemoveClaim: (assignee: UserInfo, id: string) => void;
  onClaimQueue: (id: string) => void;
}

const getMostRecentObj = (array: any[]) => {
  if (!array?.length) return undefined;
  return array?.reduce((prev: any, current: any) => {
    return prev.date > current.date ? prev : current;
  });
};

const accountAttributesStatuses = {
  [DomesticContentEligibilityTypes.confirmed]: 'Domestic Content',
  [DomesticContentEligibilityTypes.eligible]: 'Domestic Content',
  [DomesticContentEligibilityTypes.underConsideration]: 'Domestic Content',
  [DomesticContentEligibilityTypes.epcOptOut]: 'Domestic Content - Opt Out',
  [AIPackageReviewStatus.completed]: '8090 - Completed',
  [AIPackageReviewStatus.pending]: '8090 - Processing',
  [AIPackageReviewStatus.processing]: '8090 - Processing',
  [AIPackageReviewStatus.failed]: '8090 - Failed',
  [AIPackageReviewStatus.completedWithFailures]: '8090 - Completed',
};

const AssigneeCell: React.FC<AssigneeCellProps> = ({ cell, id, onRemoveClaim, onClaimQueue }) => {
  const handleRemoveClaim = useCallback(() => {
    if (cell) {
      onRemoveClaim(cell, id);
    }
  }, [cell, id, onRemoveClaim]);
  const handleClaimQueue = useCallback(() => {
    onClaimQueue(id);
  }, [id, onClaimQueue]);

  if (cell) {
    return (
      <Box direction="row" gap="4xs" alignItems="center" fontSize="xs">
        <Box>{cell.name}</Box>
        <Button
          variant="tertiary"
          tone="neutral"
          iconPrefix="c-remove"
          aria-label="remove"
          size="xs"
          onClick={handleRemoveClaim}
        />
      </Box>
    );
  }

  return (
    <Button size="xs" variant="primary" tone="neutral" onClick={handleClaimQueue}>
      Claim
    </Button>
  );
};

const getLatestReviewers = (queue: any): { id: string; name: string }[] => {
  const currentAssigneeId = queue?.assignee?.id;
  const claimedByHistory =
    queue?.history
      ?.filter(
        (historyItem: any) => historyItem.action === QueueHistoryAction.claimedBy && historyItem.changedTo !== null,
      )
      ?.toSorted((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime()) ?? [];
  const uniqueReviewers: { id: string; name: string }[] = [];
  const dedupedReviewers = new Set<string>();

  for (const claimItem of claimedByHistory) {
    const userId = claimItem?.user?.id;
    const name = claimItem?.user?.name;

    if (!userId || userId === currentAssigneeId) continue;
    if (dedupedReviewers.has(userId)) continue;
    dedupedReviewers.add(userId);
    uniqueReviewers.push({ id: userId, name: name || userId });

    if (uniqueReviewers.length === 3) break;
  }
  return uniqueReviewers;
};

const dateColumnRender = (cell: any) => (
  <DateTimeDisplay value={cell} fontSize="xs" dateFormat="DD" showDuration inline />
);

export const getQueueTableColumns = ({
  queueProps,
  extraColumns,
  removeClaim,
  claimQueue,
  handleQueueRowSelection,
  handleAllQueueRowSelection,
  selectedQueueIds,
  queues,
  showCheckbox,
  qcReviewHoldMotion,
  isFirstSubmittedSort,
  isOnHoldSort,
  isHoldQueueView,
  showTargetReviewTime,
}: GetQueueTableColumnsProps) => {
  const rowCheckboxHandler = (rowId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handleQueueRowSelection([rowId], event.target.checked);
  };

  const firstSubmittedColumn = { heading: 'first submitted', dataKey: 'createdAt', render: dateColumnRender };
  const lastSubmittedColumn = { heading: 'last submitted', dataKey: 'lastSubmittedAt', render: dateColumnRender };
  const daysOnHoldColumn = { heading: 'days on hold', dataKey: 'placedOnHoldAt', render: dateColumnRender };

  // The trailing date columns are one unit whose order reflects whichever sort is active, so the
  // sorted-by field is always leftmost within the trio (or pair, outside the Hold Queue view).
  const dateColumns = isHoldQueueView
    ? isOnHoldSort
      ? [daysOnHoldColumn, firstSubmittedColumn, lastSubmittedColumn]
      : isFirstSubmittedSort
        ? [firstSubmittedColumn, lastSubmittedColumn, daysOnHoldColumn]
        : [lastSubmittedColumn, firstSubmittedColumn, daysOnHoldColumn]
    : isFirstSubmittedSort
      ? [firstSubmittedColumn, lastSubmittedColumn]
      : [lastSubmittedColumn, firstSubmittedColumn];

  return [
    ...(showCheckbox
      ? [
          {
            heading: (
              <Checkbox
                id="selectAll"
                isChecked={Boolean(queues.length && selectedQueueIds.length === queues.length)}
                label=""
                onChange={handleAllQueueRowSelection}
                size="sm"
              />
            ),
            dataKey: 'id',
            render: (_cell: any, row: any) => {
              return (
                <Checkbox
                  id={row.id}
                  isChecked={selectedQueueIds.includes(row.id)}
                  label=""
                  onChange={rowCheckboxHandler(row.id)}
                  size="sm"
                />
              );
            },
          },
        ]
      : []),
    {
      heading: 'status',
      cellClassName: 'queueStatus',
      render: (_: any, row: any) => {
        const mostRecentHoldOrRejectionObj =
          row?.status === QueueStatus.rejected
            ? getMostRecentObj(row?.rejectionReasons)
            : row?.status === QueueStatus.onHold
              ? getMostRecentObj(row?.holdReasons)
              : undefined;
        return (
          <Box childGap="xs" alignItems="center" className={row.status}>
            <Box alignItems="center" childGap="xs">
              {row.status === 'onHold' && <Icon name="pause" size="lg" color="warning-500" />}
              {row.status === 'rejected' && <Icon name="remove" size="lg" color="danger" />}
              <Box textAlign="center" fontSize="sm" fontWeight="medium">
                {queueStatusMap[row.status as QueueStatus]}
              </Box>
            </Box>
            {mostRecentHoldOrRejectionObj?.reasons ? (
              <Box fontWeight="medium" fontSize="xs">
                {mostRecentHoldOrRejectionObj.reasons.join(', ')}
              </Box>
            ) : null}
            <Box>
              <DateTimeDisplay value={row.updatedAt} showDuration fontSize="xs" dateFormat="DD h:mm:ss a" inline />
            </Box>
          </Box>
        );
      },
    },
    {
      heading: 'Account',
      dataKey: 'account',
      render: (cell: any, row: any) => {
        return (
          <Box display="block" childGap="2xs" fontSize="xs" padding="sm 0" color="body-secondary">
            <Box display="block" fontWeight="medium" fontSize="sm">
              <Link to={queueProps.getAccountReviewLink(cell.id, row.type)}>{cell.primaryApplicantName}</Link>
            </Box>
            {cell.address && (
              <Box childGap="2xs">
                <Box>{cell.address.address1}</Box>
                <Box>
                  {cell.address.city} {cell.address.state} {cell.address.zip}
                </Box>
              </Box>
            )}
            {cell.applicants?.length && cell.applicants[0].email && <Box>{cell?.applicants[0]?.email}</Box>}
            {cell.externalReference && <Box>{`Ext. Reference: ${cell.externalReference}`}</Box>}
          </Box>
        );
      },
    },
    {
      heading: 'claimed by',
      dataKey: 'assignee',
      render: (cell: any, row: any) => {
        const latestReviewers = getLatestReviewers(row);
        return (
          <Box gap="xs" width="2xl">
            <Box width="lg">
              <AssigneeCell cell={cell} id={row.id} onRemoveClaim={removeClaim} onClaimQueue={claimQueue} />
            </Box>
            {qcReviewHoldMotion && latestReviewers.length > 0 && (
              <Box gap="2xs" fontSize="xs">
                <Box fontWeight="medium">Previously reviewed by: </Box>
                {latestReviewers.map((reviewer) => (
                  <Box key={reviewer.id} direction="row" gap="2xs" alignItems="center">
                    <Box>{reviewer.name}</Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      heading: 'account attributes',
      render: (_: any, row: any) => {
        return (
          <Box>
            {(row?.account?.programType === ProgramType.hvac ||
              row?.account?.programType === ProgramType.waterHeater ||
              row?.account?.programType === ProgramType.generator) && (
              <Box fontSize="xs" padding="sm 0" color="body-secondary">
                {row?.account?.systemsTotal > 1 ? `${row.account.systemsTotal} Total Systems` : ''}
              </Box>
            )}
            {row?.account?.payeeContractor && (
              <Box fontSize="xs" padding="sm 0" color="body-secondary">
                {`Contractor: ${row.account.payeeContractor.name || row.account.payeeContractor.alias}`}
              </Box>
            )}
            {row?.attributes?.hasSubsidyTariff && (
              <Box fontSize="xs" padding="sm 0" color="body-secondary">
                Subsidized Tariff
              </Box>
            )}
            <Box fontSize="xs" padding="sm 0" color="body-secondary">
              {accountAttributesStatuses[
                row?.account?.domesticContentEligibilityStatus as keyof typeof accountAttributesStatuses
              ] || ''}
            </Box>
            <Box fontSize="xs" padding="sm 0" color="body-secondary">
              {accountAttributesStatuses[row?.aiPackageReviewStatus as keyof typeof accountAttributesStatuses] || ''}
            </Box>
          </Box>
        );
      },
    },
    ...(extraColumns || []),
    {
      heading: 'licensed org',
      render: (_: any, { account }: any) => {
        return (
          <Box fontSize="xs" padding="sm 0" color="body-secondary">
            <Box>{account.licensedOrganizationName}</Box>
          </Box>
        );
      },
    },
    ...(showTargetReviewTime
      ? [
          {
            heading: 'target review time',
            render: (_: any, row: any) => {
              const targetDate = row?.reviewSla?.current?.targetDate;
              return targetDate ? (
                <DateTimeDisplay value={targetDate} fontSize="xs" dateFormat="DD h:mm a" inline />
              ) : null;
            },
          },
        ]
      : []),
    ...dateColumns,
  ];
};
